import React, { useCallback, useEffect, useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { BulkChangeTicketTypeRuleDialog } from 'src/dialogs/BulkEdits/BulkChangeTicketTypeRuleDialog';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { useUserCanSetDeliveryStrategy } from 'src/hooks/useUserHasListingPermissions';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  BulkEditListingClient,
  BulkEditPreviewWithDetails,
  CommandOptionType,
  DeliveryType,
  Event,
  ListingQuery,
  TicketTypeOverride,
} from 'src/WebApiController';

import { INVENTORY_BULK_UPDATE_TICKET_TYPE_RULE_UPDATE_KEY } from '../../InventoryActionDropdown.constants';
import { BulkActionItem } from '../types';
import { LaunchChangeTicketTypeRule } from './LaunchChangeTicketTypeRule';

export const useBulkChangeTicketTypeRuleAction = (
  filterQueryWithEventIds: ListingQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { registerCommand, unregisterCommand } = useCommandMenuContext();
  const hasPermission = useUserCanSetDeliveryStrategy(undefined, true);

  const { activeAccountWebClientConfig } = useAppContext();
  const updateKey = event
    ? `ticket-type-rule-event-${event.virtId}`
    : INVENTORY_BULK_UPDATE_TICKET_TYPE_RULE_UPDATE_KEY;
  const {
    refreshCatalog,
    eventsExpansion: { setListItemExpansion, refreshExpandedListItems },
  } = useCatalogDataContext();
  const { showErrorDialog } = useClientLogContext();

  const {
    openDialog: openBulkChangeTicketTypeRuleDialog,
    closeDialog: closeBulkChangeTicketTypeRuleDialog,
  } = useDialog(
    DialogId.BulkChangeTicketTypeRule,
    BulkChangeTicketTypeRuleDialog
  );

  const onSubmit = useCallback(
    async (
      deliveryType: DeliveryType | null,
      ticketTypeRules: TicketTypeOverride | null,
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      updateSalesOfListing?: boolean,
      preview?: BulkEditPreviewWithDetails
    ) => {
      setIsLoading(true);
      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const client = new BulkEditListingClient(
              activeAccountWebClientConfig
            );

            const preview = await client.bulkUpdateTicketTypeRulesPreview(
              filterQueryWithEventIds,
              updateSalesOfListing
            );

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkUpdateTicketTypeRulesPreview', error, {
              trackErrorData: { filterQueryWithEventIds },
            });
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      } else if (deliveryType && ticketTypeRules) {
        await tryInvokeApi(
          async () => {
            const succeeded = await new BulkEditListingClient(
              activeAccountWebClientConfig
            ).bulkUpdateTicketTypeRules(
              {
                item1: preview!.preview,
                item2: deliveryType,
                item3:
                  ticketTypeRules.overrides[0].ticketTypePriorities ?? null,
              },
              updateKey,
              supportBackgroundProcess,
              updateSalesOfListing
            );
            if (!supportBackgroundProcess) {
              if (succeeded) {
                if (!event) {
                  await refreshCatalog();
                }
                await refreshExpandedListItems();
              }

              closeBulkChangeTicketTypeRuleDialog();
            }
          },
          (error) => {
            showErrorDialog(
              'BulkEditListingClient.bulkUpdateTicketTypeRules',
              error,
              {
                trackErrorData: {
                  preview,
                  deliveryType,
                  ticketTypeRules:
                    ticketTypeRules.overrides[0].ticketTypePriorities ?? null,
                },
              }
            );
          },
          () => {
            if (!supportBackgroundProcess) {
              setIsLoading(false);
            }
          }
        );
      }
    },
    [
      setIsLoading,
      activeAccountWebClientConfig,
      filterQueryWithEventIds,
      showErrorDialog,
      updateKey,
      closeBulkChangeTicketTypeRuleDialog,
      event,
      refreshExpandedListItems,
      refreshCatalog,
    ]
  );

  const openDialogStableRef = useEventCallback(() => {
    if (event) {
      setListItemExpansion(true, [event.virtId]);
    }
    openBulkChangeTicketTypeRuleDialog({
      event,
      updateKey,
      onOkay: onSubmit,
      onClose: () => {
        setIsLoading(false);
      },
      onCancel: closeBulkChangeTicketTypeRuleDialog,
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.ListingBulkEditTicketTypeRule,
        onSelect: openDialogStableRef,
      },
      disabled: !hasPermission,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.ListingBulkEditTicketTypeRule,
        disabled: !hasPermission,
      });
    };
  }, [hasPermission, openDialogStableRef, registerCommand, unregisterCommand]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (event) {
        setListItemExpansion(true, [event.virtId]);
      }
      openBulkChangeTicketTypeRuleDialog({
        event,
        updateKey,
        onOkay: onSubmit,
        onClose: () => {
          setIsLoading(false);
        },
        onCancel: closeBulkChangeTicketTypeRuleDialog,
      });
    },
    [
      closeBulkChangeTicketTypeRuleDialog,
      event,
      onSubmit,
      openBulkChangeTicketTypeRuleDialog,
      setIsLoading,
      setListItemExpansion,
      updateKey,
    ]
  );

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasPermission) {
      return [];
    }
    return [
      {
        title: ContentId.SetDeliveryStrategy,
        disabled: isLoading,
        onClick,
      },
    ];
  }, [hasPermission, isLoading, onClick]);

  return {
    dropDown: (
      <LaunchChangeTicketTypeRule
        key="ChangeTicketTypeRule"
        onClick={onClick}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
